/**
 * Container Template Library
 * Pre-configured container templates for common use cases
 */

export type ContainerType = 'cloud-provider' | 'layer' | 'network-zone' | 'service-group'
export type CloudProvider = 'aws' | 'azure' | 'gcp' | 'generic'

export interface ContainerTemplate {
  id: string
  name: string
  description: string
  type: ContainerType
  provider?: CloudProvider
  icon: string
  size: { width: number; height: number }
  children?: ContainerTemplate[] // For nested template support
  category: 'cloud' | 'architecture' | 'network' | 'services'
}

/**
 * Complete library of container templates
 */
export const CONTAINER_TEMPLATES: ContainerTemplate[] = [
  // ============================================================================
  // CLOUD PROVIDERS
  // ============================================================================
  {
    id: 'aws-vpc',
    name: 'AWS VPC',
    description: 'Amazon Web Services Virtual Private Cloud',
    type: 'cloud-provider',
    provider: 'aws',
    icon: '☁️',
    size: { width: 800, height: 600 },
    category: 'cloud',
    children: [
      {
        id: 'aws-public-subnet',
        name: 'Public Subnet',
        description: 'Public-facing network zone',
        type: 'network-zone',
        icon: '🌐',
        size: { width: 350, height: 250 },
        category: 'network'
      },
      {
        id: 'aws-private-subnet',
        name: 'Private Subnet',
        description: 'Internal network zone',
        type: 'network-zone',
        icon: '🔒',
        size: { width: 350, height: 250 },
        category: 'network'
      }
    ]
  },
  {
    id: 'aws-region',
    name: 'AWS Region',
    description: 'AWS geographic region',
    type: 'cloud-provider',
    provider: 'aws',
    icon: '☁️',
    size: { width: 700, height: 500 },
    category: 'cloud'
  },
  {
    id: 'azure-resource-group',
    name: 'Azure Resource Group',
    description: 'Azure resource grouping container',
    type: 'cloud-provider',
    provider: 'azure',
    icon: '⛅',
    size: { width: 700, height: 500 },
    category: 'cloud'
  },
  {
    id: 'azure-subscription',
    name: 'Azure Subscription',
    description: 'Azure subscription boundary',
    type: 'cloud-provider',
    provider: 'azure',
    icon: '⛅',
    size: { width: 900, height: 700 },
    category: 'cloud'
  },
  {
    id: 'gcp-project',
    name: 'GCP Project',
    description: 'Google Cloud Platform project',
    type: 'cloud-provider',
    provider: 'gcp',
    icon: '☁️',
    size: { width: 700, height: 500 },
    category: 'cloud'
  },
  {
    id: 'gcp-vpc-network',
    name: 'GCP VPC Network',
    description: 'Google Cloud VPC network',
    type: 'cloud-provider',
    provider: 'gcp',
    icon: '☁️',
    size: { width: 800, height: 600 },
    category: 'cloud'
  },
  {
    id: 'generic-cloud',
    name: 'Cloud Environment',
    description: 'Generic cloud provider container',
    type: 'cloud-provider',
    provider: 'generic',
    icon: '☁️',
    size: { width: 700, height: 500 },
    category: 'cloud'
  },

  // ============================================================================
  // ARCHITECTURAL LAYERS
  // ============================================================================
  {
    id: 'frontend-layer',
    name: 'Frontend Layer',
    description: 'Client-side application layer',
    type: 'layer',
    icon: '📦',
    size: { width: 600, height: 300 },
    category: 'architecture'
  },
  {
    id: 'backend-layer',
    name: 'Backend Services',
    description: 'Server-side services and APIs',
    type: 'layer',
    icon: '⚙️',
    size: { width: 600, height: 300 },
    category: 'architecture'
  },
  {
    id: 'database-layer',
    name: 'Database Layer',
    description: 'Data persistence layer',
    type: 'layer',
    icon: '🗄️',
    size: { width: 600, height: 250 },
    category: 'architecture'
  },
  {
    id: 'api-gateway-layer',
    name: 'API Gateway Layer',
    description: 'API gateway and routing',
    type: 'layer',
    icon: '🚪',
    size: { width: 600, height: 200 },
    category: 'architecture'
  },
  {
    id: 'infrastructure-layer',
    name: 'Infrastructure Layer',
    description: 'Infrastructure components',
    type: 'layer',
    icon: '🏗️',
    size: { width: 600, height: 300 },
    category: 'architecture'
  },
  {
    id: 'presentation-layer',
    name: 'Presentation Layer',
    description: 'User interface components',
    type: 'layer',
    icon: '🖼️',
    size: { width: 600, height: 300 },
    category: 'architecture'
  },
  {
    id: 'business-logic-layer',
    name: 'Business Logic Layer',
    description: 'Core business logic',
    type: 'layer',
    icon: '💼',
    size: { width: 600, height: 300 },
    category: 'architecture'
  },

  // ============================================================================
  // NETWORK ZONES
  // ============================================================================
  {
    id: 'dmz-zone',
    name: 'DMZ Zone',
    description: 'Demilitarized zone for public-facing services',
    type: 'network-zone',
    icon: '🌐',
    size: { width: 500, height: 350 },
    category: 'network'
  },
  {
    id: 'public-zone',
    name: 'Public Zone',
    description: 'Publicly accessible network zone',
    type: 'network-zone',
    icon: '🌐',
    size: { width: 500, height: 350 },
    category: 'network'
  },
  {
    id: 'private-zone',
    name: 'Private Zone',
    description: 'Internal private network zone',
    type: 'network-zone',
    icon: '🔒',
    size: { width: 500, height: 350 },
    category: 'network'
  },
  {
    id: 'secure-zone',
    name: 'Secure Zone',
    description: 'High-security network zone',
    type: 'network-zone',
    icon: '🔐',
    size: { width: 500, height: 350 },
    category: 'network'
  },
  {
    id: 'management-zone',
    name: 'Management Zone',
    description: 'Management and monitoring zone',
    type: 'network-zone',
    icon: '🔧',
    size: { width: 500, height: 300 },
    category: 'network'
  },

  // ============================================================================
  // SERVICE GROUPS
  // ============================================================================
  {
    id: 'microservices-group',
    name: 'Microservices',
    description: 'Microservices cluster',
    type: 'service-group',
    icon: '⚙️',
    size: { width: 550, height: 400 },
    category: 'services'
  },
  {
    id: 'auth-services-group',
    name: 'Authentication Services',
    description: 'Auth and identity services',
    type: 'service-group',
    icon: '🔑',
    size: { width: 500, height: 350 },
    category: 'services'
  },
  {
    id: 'data-services-group',
    name: 'Data Services',
    description: 'Data processing services',
    type: 'service-group',
    icon: '📊',
    size: { width: 550, height: 400 },
    category: 'services'
  },
  {
    id: 'messaging-services-group',
    name: 'Messaging Services',
    description: 'Message queues and event buses',
    type: 'service-group',
    icon: '📨',
    size: { width: 500, height: 350 },
    category: 'services'
  },
  {
    id: 'monitoring-services-group',
    name: 'Monitoring & Logging',
    description: 'Observability services',
    type: 'service-group',
    icon: '📈',
    size: { width: 500, height: 350 },
    category: 'services'
  }
]

/**
 * Get templates by category
 */
export function getTemplatesByCategory(category: ContainerTemplate['category']): ContainerTemplate[] {
  return CONTAINER_TEMPLATES.filter(t => t.category === category)
}

/**
 * Get template by ID
 */
export function getTemplateById(id: string): ContainerTemplate | undefined {
  return CONTAINER_TEMPLATES.find(t => t.id === id)
}

/**
 * Get cloud provider templates
 */
export function getCloudProviderTemplates(provider?: CloudProvider): ContainerTemplate[] {
  if (provider) {
    return CONTAINER_TEMPLATES.filter(t => t.type === 'cloud-provider' && t.provider === provider)
  }
  return CONTAINER_TEMPLATES.filter(t => t.type === 'cloud-provider')
}

/**
 * Get layer templates
 */
export function getLayerTemplates(): ContainerTemplate[] {
  return CONTAINER_TEMPLATES.filter(t => t.type === 'layer')
}

/**
 * Get network zone templates
 */
export function getNetworkZoneTemplates(): ContainerTemplate[] {
  return CONTAINER_TEMPLATES.filter(t => t.type === 'network-zone')
}

/**
 * Get service group templates
 */
export function getServiceGroupTemplates(): ContainerTemplate[] {
  return CONTAINER_TEMPLATES.filter(t => t.type === 'service-group')
}
